import { useEffect, useMemo, useState, useRef, type CSSProperties } from 'react';
import toast from 'react-hot-toast';
import { Search, Plus, X, Eye } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import { Product } from '@/types';
import { Unit, formatPricePerUnit } from '@/utils/units';
import {
  LabelConfig,
  LabelData,
  DEFAULT_LABEL_CONFIG,
  buildLabelZpl,
  buildBatchZpl,
  buildFontDownloadZpl,
} from '@/utils/zplLabels';

// Separa el peso/presentación del nombre. Si el nombre trae "500 gr", lo saca y lo
// devuelve aparte, dejando el nombre sin ese dato (para no repetirlo en la etiqueta).
function splitNameWeight(rawName: string): { name: string; weight: string } {
  const re = /(\d+[.,]?\d*)\s?(kgs?|grs?|gramos?|g|ml|cc|lt?s?|litros?)\b/i;
  const m = rawName.match(re);
  if (!m) return { name: rawName.trim(), weight: '' };
  const weight = `${m[1]} ${m[2].toLowerCase()}`;
  const cleaned = rawName
    .replace(m[0], ' ')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s*[-·|,]\s*$/, '')   // separadores colgando al final
    .replace(/^\s*[-·|,]\s*/, '')   // o al principio
    .trim();
  return { name: cleaned || rawName.trim(), weight };
}

// Aproxima cada tipografía ZPL a un estilo CSS para que la vista previa aproximada
// muestre la diferencia al cambiar de fuente. (La exacta la da "Vista previa real".)
function cssFontFor(f: string): CSSProperties {
  switch (f) {
    case 'A': // fina / condensada
      return { fontFamily: "'Arial Narrow', 'Roboto Condensed', sans-serif", letterSpacing: '-0.5px' };
    case 'D': // ancha
      return { fontFamily: 'Arial, sans-serif', fontWeight: 800, letterSpacing: '0.5px' };
    case 'F': // alargada
      return { fontFamily: 'Arial, sans-serif', fontWeight: 600, letterSpacing: '1px' };
    case 'E': // OCR-B
      return { fontFamily: "'Courier New', monospace" };
    case 'H': // OCR-A
      return { fontFamily: "'Courier New', monospace", fontWeight: 700 };
    case '@': // TrueType personalizada: Microsoft JhengHei UI
      return { fontFamily: "'Microsoft JhengHei UI', 'Microsoft JhengHei', 'PingFang TC', sans-serif" };
    default: // '0' Triumvirate (estándar)
      return { fontFamily: 'Arial, Helvetica, sans-serif' };
  }
}

// Fecha ISO (yyyy-mm-dd) -> mm/yyyy (para el vencimiento de la etiqueta)
function fmtExpiration(iso?: string): string {
  if (!iso) return '';
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '';
  return `${m[2]}/${m[1]}`;
}

function toLabelData(p: Product): LabelData {
  const unit = (p.display_unit as Unit) || 'unit';
  const price = p.sale_price != null ? formatPricePerUnit(p.sale_price, unit, false) : '';
  const { name, weight } = splitNameWeight(p.name);
  // Fraccionado: el gramaje no está en el nombre; usar los gramos del paquete.
  const fracWeight = p.source_product_id && p.fraction_grams ? `${Number(p.fraction_grams)} g` : '';
  return {
    name,
    priceText: price,
    weightText: weight || fracWeight,
    expirationText: fmtExpiration(p.expiration_date),
    barcodeText: p.code || undefined,
  };
}

// Impresión directa vía Zebra Browser Print (servicio local en la PC de la impresora).
// Si la app corre en HTTPS hay que usar el canal seguro (9101); si no, el HTTP (9100).
async function printViaBrowserPrint(zpl: string): Promise<void> {
  const bases = ['https://localhost:9101', 'http://localhost:9100'];
  let lastErr: unknown = null;
  for (const base of bases) {
    try {
      // Tomar la primera impresora disponible (no depende de marcar "default")
      const avail = await fetch(`${base}/available`);
      if (!avail.ok) continue;
      const data = await avail.json();
      const device = data?.printer?.[0];
      if (!device) { lastErr = new Error('sin impresoras'); continue; }
      const write = await fetch(`${base}/write`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device, data: zpl }),
      });
      if (write.ok) return; // enviado
      lastErr = new Error('write failed');
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr ?? new Error('Browser Print no disponible');
}

export default function LabelPrintModal({
  isOpen,
  onClose,
  products,
}: {
  isOpen: boolean;
  onClose: () => void;
  products: Product[];
}) {
  // Recordar la última configuración usada (para no editar siempre)
  const [cfg, setCfg] = useState<LabelConfig>(() => {
    try {
      const saved = localStorage.getItem('labelConfig');
      if (saved) return { ...DEFAULT_LABEL_CONFIG, ...JSON.parse(saved) };
    } catch { /* ignore */ }
    return DEFAULT_LABEL_CONFIG;
  });
  useEffect(() => {
    try { localStorage.setItem('labelConfig', JSON.stringify(cfg)); } catch { /* ignore */ }
  }, [cfg]);
  const [printing, setPrinting] = useState(false);
  const [realPreview, setRealPreview] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Etiquetas de texto libre (además de las de productos)
  const [customLabels, setCustomLabels] = useState<{ id: number; name: string; priceText: string; qty: number }[]>([]);
  const [newText, setNewText] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [newQty, setNewQty] = useState('1');
  const [nextCustomId, setNextCustomId] = useState(1);
  // Etiqueta enfocada para la vista previa (si es null, se usa la primera del lote)
  const [previewFocus, setPreviewFocus] = useState<LabelData | null>(null);

  // Elegir qué productos imprimir (por defecto todos los seleccionados)
  const [chosen, setChosen] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState('');
  useEffect(() => {
    if (isOpen) {
      setChosen(new Set(products.map((p) => p.id)));
      setSearch('');
      setRealPreview(null);
      setCustomLabels([]);
      setNewText(''); setNewPrice(''); setNewQty('1');
      setPreviewFocus(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const addCustomLabel = () => {
    const name = newText.trim();
    if (!name) { toast.error('Escribí el texto de la etiqueta'); return; }
    const qty = Math.max(1, Math.round(Number(newQty) || 1));
    setCustomLabels((prev) => [...prev, { id: nextCustomId, name, priceText: newPrice.trim(), qty }]);
    setNextCustomId((n) => n + 1);
    setPreviewFocus({ name, priceText: newPrice.trim() || undefined });
    setRealPreview(null);
    setNewText(''); setNewPrice(''); setNewQty('1');
  };
  const removeCustomLabel = (id: number) => setCustomLabels((prev) => prev.filter((c) => c.id !== id));

  const activeProducts = useMemo(() => products.filter((p) => chosen.has(p.id)), [products, chosen]);
  const visibleProducts = useMemo(
    () => products.filter((p) => !search || p.name.toLowerCase().includes(search.toLowerCase()) || (p.code || '').toLowerCase().includes(search.toLowerCase())),
    [products, search]
  );

  // Etiquetas de texto libre expandidas por su cantidad
  const customLabelData = useMemo<LabelData[]>(
    () => customLabels.flatMap((c) => Array(Math.max(1, c.qty)).fill(0).map(() => ({ name: c.name, priceText: c.priceText || undefined }))),
    [customLabels]
  );
  const labels = useMemo(() => [...activeProducts.map(toLabelData), ...customLabelData], [activeProducts, customLabelData]);
  // Etiqueta que se muestra en la vista previa
  const previewData = previewFocus ?? labels[0];
  const zpl = useMemo(() => buildBatchZpl(labels, cfg), [labels, cfg]);
  const previewZpl = useMemo(() => (previewData ? buildLabelZpl(previewData, cfg) : ''), [previewData, cfg]);

  // Vista previa real: renderiza el ZPL exacto a imagen con Labelary
  const renderRealPreview = async (silent = false) => {
    if (!previewData) return;
    setLoadingPreview(true);
    try {
      const dpmm = Math.max(6, Math.round(cfg.dpi / 25.4)); // 203->8, 300->12
      const wIn = (cfg.widthMm / 25.4).toFixed(2);
      const hIn = (cfg.heightMm / 25.4).toFixed(2);
      const single = buildLabelZpl(previewData, { ...cfg, copies: 1 });
      const res = await fetch(`https://api.labelary.com/v1/printers/${dpmm}dpmm/labels/${wIn}x${hIn}/0/`, {
        method: 'POST',
        headers: { Accept: 'image/png' },
        body: single,
      });
      if (!res.ok) throw new Error('labelary');
      const blob = await res.blob();
      setRealPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(blob); });
    } catch {
      if (!silent) toast.error('No se pudo generar la vista previa real');
    } finally {
      setLoadingPreview(false);
    }
  };

  // Vista previa real automática: al abrir o al cambiar la configuración/etiqueta,
  // regenera la imagen exacta (con un pequeño retardo para no spamear la API).
  useEffect(() => {
    if (!isOpen || !previewData) return;
    const t = setTimeout(() => { renderRealPreview(true); }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, cfg, previewData]);

  const set = (patch: Partial<LabelConfig>) => setCfg((c) => ({ ...c, ...patch }));

  const totalEtiquetas = labels.length * Math.max(1, cfg.copies);
  const allVisibleChosen = visibleProducts.length > 0 && visibleProducts.every((p) => chosen.has(p.id));
  const toggleChosen = (id: number) =>
    setChosen((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAllVisible = () =>
    setChosen((prev) => {
      const n = new Set(prev);
      if (allVisibleChosen) visibleProducts.forEach((p) => n.delete(p.id));
      else visibleProducts.forEach((p) => n.add(p.id));
      return n;
    });

  const copyZpl = async () => {
    try {
      await navigator.clipboard.writeText(zpl);
      toast.success('ZPL copiado al portapapeles');
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  // Cargar una fuente TrueType (.ttf) a la memoria de la impresora (se hace UNA vez).
  const fontInputRef = useRef<HTMLInputElement>(null);
  const [uploadingFont, setUploadingFont] = useState(false);
  const uploadFontToPrinter = async (file: File) => {
    setUploadingFont(true);
    try {
      const buf = new Uint8Array(await file.arrayBuffer());
      await printViaBrowserPrint(buildFontDownloadZpl(buf));
      toast.success('Fuente enviada a la impresora. Probá imprimir una etiqueta.');
    } catch {
      toast.error('No se pudo cargar la fuente (¿Browser Print activo? ¿fuente muy grande para la impresora?)');
    } finally {
      setUploadingFont(false);
      if (fontInputRef.current) fontInputRef.current.value = '';
    }
  };

  const downloadZpl = () => {
    const blob = new Blob([zpl], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `etiquetas-${activeProducts.length}.zpl`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printDirect = async () => {
    setPrinting(true);
    try {
      await printViaBrowserPrint(zpl);
      toast.success('Enviado a la impresora');
    } catch {
      toast.error('No se detectó Zebra Browser Print. Usá "Descargar ZPL" o instalalo.');
    } finally {
      setPrinting(false);
    }
  };

  // Preview visual aproximado (proporción de la etiqueta)
  const aspect = cfg.widthMm / cfg.heightMm;
  const previewW = 220;
  const previewH = Math.round(previewW / aspect);
  const first = previewData;
  const fontCss = cssFontFor(cfg.font);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Imprimir etiquetas (Zebra)" size="lg">
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          {activeProducts.length} producto{activeProducts.length !== 1 ? 's' : ''}
          {customLabels.length > 0 && ` + ${customLabelData.length} de texto`}
          {' · '}{totalEtiquetas} etiqueta{totalEtiquetas !== 1 ? 's' : ''} a imprimir
        </p>

        {/* Elegir qué productos imprimir */}
        <div className="border border-gray-200 rounded-xl">
          <div className="flex items-center gap-2 p-2 border-b border-gray-100">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar producto…"
                className="w-full pl-8 pr-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <button type="button" onClick={toggleAllVisible} className="text-sm font-medium text-primary-600 hover:text-primary-700 whitespace-nowrap px-2">
              {allVisibleChosen ? 'Quitar todos' : 'Elegir todos'}
            </button>
          </div>
          <div className="max-h-40 overflow-y-auto divide-y divide-gray-50">
            {visibleProducts.map((p) => (
              <label key={p.id} className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-gray-50 text-sm">
                <input type="checkbox" checked={chosen.has(p.id)} onChange={() => toggleChosen(p.id)} className="rounded border-gray-300 text-primary-600" />
                <span className="flex-1 truncate text-gray-800">{p.name}</span>
                {p.sale_price != null && (
                  <span className="text-gray-400 text-xs">{formatPricePerUnit(p.sale_price, (p.display_unit as Unit) || 'unit')}</span>
                )}
              </label>
            ))}
          </div>
        </div>

        {/* Etiquetas de texto libre */}
        <div className="border border-gray-200 rounded-xl p-3 space-y-2">
          <p className="text-sm font-medium text-gray-700">Etiquetas de texto (además de los productos)</p>
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[9rem]">
              <label className="block text-xs text-gray-500 mb-1">Texto</label>
              <input
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomLabel(); } }}
                placeholder="Ej: OFERTA · SIN TACC · un nombre…"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="w-32">
              <label className="block text-xs text-gray-500 mb-1">Precio/2da línea</label>
              <input
                value={newPrice}
                onChange={(e) => setNewPrice(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomLabel(); } }}
                placeholder="$1.500 (opc.)"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="w-20">
              <label className="block text-xs text-gray-500 mb-1">Cant.</label>
              <input
                type="number"
                min="1"
                value={newQty}
                onChange={(e) => setNewQty(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <Button type="button" variant="secondary" onClick={addCustomLabel}>
              <Plus className="h-4 w-4 mr-1" /> Agregar
            </Button>
          </div>
          {customLabels.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              {customLabels.map((c) => (
                <span key={c.id} className="inline-flex items-center gap-1.5 bg-gray-100 rounded-full pl-3 pr-1 py-1 text-xs">
                  <span className="font-medium text-gray-800 max-w-[10rem] truncate">{c.name}</span>
                  {c.priceText && <span className="text-gray-500">{c.priceText}</span>}
                  <span className="text-gray-400">×{c.qty}</span>
                  <button type="button" title="Ver en la vista previa" onClick={() => { setPreviewFocus({ name: c.name, priceText: c.priceText || undefined }); setRealPreview(null); }} className="p-0.5 text-gray-400 hover:text-primary-600">
                    <Eye className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" title="Quitar" onClick={() => removeCustomLabel(c.id)} className="p-0.5 text-gray-400 hover:text-red-600">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Configuración */}
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm">
                <span className="block text-gray-600 mb-1">Ancho (mm)</span>
                <input type="number" min="10" value={cfg.widthMm}
                  onChange={(e) => set({ widthMm: Number(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </label>
              <label className="text-sm">
                <span className="block text-gray-600 mb-1">Alto (mm)</span>
                <input type="number" min="10" value={cfg.heightMm}
                  onChange={(e) => set({ heightMm: Number(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </label>
              <label className="text-sm">
                <span className="block text-gray-600 mb-1">DPI</span>
                <select value={cfg.dpi} onChange={(e) => set({ dpi: Number(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option value={203}>203 (8 dpmm)</option>
                  <option value={300}>300 (12 dpmm)</option>
                </select>
              </label>
              <label className="text-sm">
                <span className="block text-gray-600 mb-1">Copias c/u</span>
                <input type="number" min="1" value={cfg.copies}
                  onChange={(e) => set({ copies: Math.max(1, Number(e.target.value)) })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </label>
              <label className="text-sm">
                <span className="block text-gray-600 mb-1">Tipografía</span>
                <select value={cfg.font} onChange={(e) => set({ font: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option value="0">Estándar (redondeada)</option>
                  <option value="A">Fina / condensada (A)</option>
                  <option value="D">Ancha (D)</option>
                  <option value="F">Alargada (F)</option>
                  <option value="E">OCR-B (E)</option>
                  <option value="H">OCR-A (H)</option>
                  <option value="@">Microsoft JhengHei UI (TTF)</option>
                </select>
              </label>
            </div>

            {/* Carga de la fuente TrueType a la impresora (solo si se eligió TTF) */}
            {cfg.font === '@' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                <p className="text-xs text-amber-800">
                  Para imprimir con <strong>Microsoft JhengHei UI</strong> hay que cargar el archivo{' '}
                  <code>.ttf</code> a la impresora <strong>una vez</strong> (queda guardado en su memoria).
                  En Windows el archivo suele estar en <code>C:\Windows\Fonts\msjh.ttf</code>.
                </p>
                <input
                  ref={fontInputRef}
                  type="file"
                  accept=".ttf,font/ttf,application/x-font-ttf"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFontToPrinter(f); }}
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  loading={uploadingFont}
                  onClick={() => fontInputRef.current?.click()}
                >
                  Cargar fuente (.ttf) a la impresora
                </Button>
                <p className="text-[11px] text-amber-700">
                  Ojo: si la fuente es muy pesada puede no entrar en la memoria de la impresora. La
                  vista previa igual la muestra. Necesita Zebra Browser Print activo.
                </p>
              </div>
            )}

            <div className="flex flex-wrap gap-3 pt-1">
              <label className="inline-flex items-center gap-1.5 text-sm">
                <input type="checkbox" checked={cfg.showName} onChange={(e) => set({ showName: e.target.checked })} className="rounded border-gray-300 text-primary-600" />
                Nombre
              </label>
              <label className="inline-flex items-center gap-1.5 text-sm">
                <input type="checkbox" checked={cfg.showWeight} onChange={(e) => set({ showWeight: e.target.checked })} className="rounded border-gray-300 text-primary-600" />
                Peso/unidad
              </label>
              <label className="inline-flex items-center gap-1.5 text-sm">
                <input type="checkbox" checked={cfg.showPrice} onChange={(e) => set({ showPrice: e.target.checked })} className="rounded border-gray-300 text-primary-600" />
                Precio
              </label>
              <label className="inline-flex items-center gap-1.5 text-sm">
                <input type="checkbox" checked={cfg.showExpiration} onChange={(e) => set({ showExpiration: e.target.checked })} className="rounded border-gray-300 text-primary-600" />
                Vencimiento
              </label>
              <label className="inline-flex items-center gap-1.5 text-sm">
                <input type="checkbox" checked={cfg.showDivider} onChange={(e) => set({ showDivider: e.target.checked })} className="rounded border-gray-300 text-primary-600" />
                Línea divisoria
              </label>
              <label className="inline-flex items-center gap-1.5 text-sm">
                <input type="checkbox" checked={cfg.showBarcode} onChange={(e) => set({ showBarcode: e.target.checked })} className="rounded border-gray-300 text-primary-600" />
                Código de barras
              </label>
            </div>

            {/* Tamaño de letra por línea */}
            <div className="space-y-2 pt-2 border-t">
              <p className="text-xs font-medium text-gray-500">Tamaño de letra</p>
              {cfg.showName && (
                <label className="flex items-center gap-2 text-xs text-gray-600">
                  <span className="w-14">Nombre</span>
                  <input type="range" min="0.4" max="1.6" step="0.1" value={cfg.nameScale}
                    onChange={(e) => set({ nameScale: Number(e.target.value) })} className="flex-1" />
                  <span className="w-8 text-right">{Math.round(cfg.nameScale * 100)}%</span>
                </label>
              )}
              {cfg.showWeight && (
                <label className="flex items-center gap-2 text-xs text-gray-600">
                  <span className="w-14">Peso</span>
                  <input type="range" min="0.4" max="1.6" step="0.1" value={cfg.weightScale}
                    onChange={(e) => set({ weightScale: Number(e.target.value) })} className="flex-1" />
                  <span className="w-8 text-right">{Math.round(cfg.weightScale * 100)}%</span>
                </label>
              )}
              {cfg.showPrice && (
                <label className="flex items-center gap-2 text-xs text-gray-600">
                  <span className="w-14">Precio</span>
                  <input type="range" min="0.4" max="1.6" step="0.1" value={cfg.priceScale}
                    onChange={(e) => set({ priceScale: Number(e.target.value) })} className="flex-1" />
                  <span className="w-8 text-right">{Math.round(cfg.priceScale * 100)}%</span>
                </label>
              )}
              {cfg.showExpiration && (
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <span className="w-14">Venc.</span>
                  <input type="range" min="0.4" max="1.6" step="0.1" value={cfg.expirationScale}
                    onChange={(e) => set({ expirationScale: Number(e.target.value) })} className="flex-1" />
                  <span className="w-8 text-right">{Math.round(cfg.expirationScale * 100)}%</span>
                  <select value={cfg.expirationAlign} onChange={(e) => set({ expirationAlign: e.target.value as 'L' | 'C' | 'R' })}
                    className="border border-gray-200 rounded px-1 py-0.5 bg-white">
                    <option value="L">Izq</option>
                    <option value="C">Centro</option>
                    <option value="R">Der</option>
                  </select>
                </div>
              )}
              {cfg.showBarcode && (
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <span className="w-14">Barras</span>
                  <input type="range" min="0.4" max="1.6" step="0.1" value={cfg.barcodeScale}
                    onChange={(e) => set({ barcodeScale: Number(e.target.value) })} className="flex-1" />
                  <span className="w-8 text-right">{Math.round(cfg.barcodeScale * 100)}%</span>
                  <select value={cfg.barcodeAlign} onChange={(e) => set({ barcodeAlign: e.target.value as 'L' | 'C' | 'R' })}
                    className="border border-gray-200 rounded px-1 py-0.5 bg-white">
                    <option value="L">Izq</option>
                    <option value="C">Centro</option>
                    <option value="R">Der</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Preview */}
          <div className="flex flex-col items-center justify-center bg-gray-50 rounded-xl p-4">
            {realPreview ? (
              <>
                <p className="text-xs text-gray-400 mb-2">Vista previa real</p>
                <img src={realPreview} alt="Etiqueta" className="border border-gray-300 bg-white max-w-full" style={{ maxHeight: 180 }} />
              </>
            ) : (
              <>
                <p className="text-xs text-gray-400 mb-2">Vista previa (aprox.)</p>
                <div
                  className="bg-white border border-gray-300 rounded-sm flex flex-col items-center justify-center text-center px-2 shadow-sm"
                  style={{ width: previewW, height: previewH, gap: Math.max(2, previewH * 0.045) }}
                >
                  {cfg.showName && first?.name && (
                    <span className="font-semibold text-gray-900 leading-tight line-clamp-2" style={{ ...fontCss, fontSize: Math.max(8, previewH * 0.15 * cfg.nameScale) }}>{first.name}</span>
                  )}
                  {cfg.showWeight && first?.weightText && (
                    <span className="text-gray-600" style={{ ...fontCss, fontSize: Math.max(8, previewH * 0.12 * cfg.weightScale) }}>{first.weightText}</span>
                  )}
                  {cfg.showDivider && (cfg.showName || cfg.showWeight) && (
                    <div className="w-full border-t border-gray-500" />
                  )}
                  {cfg.showPrice && first?.priceText && (
                    <span className="font-bold text-gray-900" style={{ ...fontCss, fontSize: Math.max(10, previewH * 0.30 * cfg.priceScale) }}>{first.priceText}</span>
                  )}
                  {cfg.showBarcode && first?.barcodeText && (
                    <div className={`flex flex-col w-full ${cfg.barcodeAlign === 'L' ? 'items-start' : cfg.barcodeAlign === 'R' ? 'items-end' : 'items-center'}`}>
                      <div style={{ height: Math.max(14, previewH * 0.26 * cfg.barcodeScale), width: '70%', minWidth: 60, background: 'repeating-linear-gradient(90deg, #111 0, #111 2px, #fff 2px, #fff 4px)' }} />
                      <span className="text-gray-600" style={{ fontSize: Math.max(7, previewH * 0.08) }}>{first.barcodeText}</span>
                    </div>
                  )}
                  {cfg.showExpiration && first?.expirationText && (
                    <span className={`text-gray-500 w-full ${cfg.expirationAlign === 'L' ? 'text-left' : cfg.expirationAlign === 'R' ? 'text-right' : 'text-center'}`} style={{ fontSize: Math.max(7, previewH * 0.09 * cfg.expirationScale) }}>Venc {first.expirationText}</span>
                  )}
                </div>
              </>
            )}
            <p className="text-[11px] text-gray-400 mt-2">{cfg.widthMm}×{cfg.heightMm}mm · {cfg.dpi}dpi</p>
            <Button variant="secondary" size="sm" className="mt-2" loading={loadingPreview} disabled={!first} onClick={() => renderRealPreview()}>
              {realPreview ? 'Actualizar vista previa' : 'Vista previa real (exacta)'}
            </Button>
            <p className="text-[10px] text-gray-400 mt-1 text-center max-w-[220px]">
              La "real" usa un servicio online (Labelary). Si no carga, la aproximada de arriba ya refleja el layout.
            </p>
          </div>
        </div>

        {/* ZPL crudo (colapsable simple) */}
        <details className="text-xs">
          <summary className="cursor-pointer text-gray-500">Ver ZPL (etiqueta de ejemplo)</summary>
          <pre className="mt-2 bg-gray-900 text-green-300 p-3 rounded-lg overflow-x-auto max-h-40">{previewZpl}</pre>
        </details>

        <div className="flex flex-wrap justify-end gap-2 pt-2 border-t">
          <Button variant="secondary" disabled={!labels.length} onClick={copyZpl}>Copiar ZPL</Button>
          <Button variant="secondary" disabled={!labels.length} onClick={downloadZpl}>Descargar ZPL</Button>
          <Button loading={printing} disabled={!labels.length} onClick={printDirect}>Imprimir en Zebra</Button>
        </div>
        <p className="text-[11px] text-gray-400">
          "Imprimir en Zebra" usa <strong>Zebra Browser Print</strong> (app gratuita de Zebra instalada en la PC de la impresora).
          Si no lo tenés, usá "Descargar ZPL" y enviá el archivo con Zebra Setup Utilities o a la cola de impresión.
        </p>
      </div>
    </Modal>
  );
}
